import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  Page,
  Card,
  PageActions,
  TextField,
  FormLayout,
  Toast,
  Loading,
  Checkbox,
  Thumbnail,
  DropZone,
  InlineError,
  Text,
  Select,
} from "@shopify/polaris";

import axios from "../../../Assets/Lib/axios";
import { useParams, useNavigate } from "react-router-dom";
import { MultiSelect } from "react-multi-select-component";
import JoditEditor from "jodit-react";
import LoadingScreen from "../../../Components/LoadingScreen";
import TagsInput from "react-tagsinput";
import "react-tagsinput/react-tagsinput.css";

const AddEditCaseStudy = ({ mode }) => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [isSaving, setIsSaving] = useState(mode === "edit");
  const [brands, setBrands] = useState([]);
  const [fileError, setFileError] = useState("");

  const editorConfig = useMemo(
    () => ({
      readonly: false,
      toolbarButtonSize: "middle",
      buttons: ["bold", "italic", "underline", "ul", "ol", "link", "fullsize"],
      showXPathInStatusbar: false,
      showCharsCounter: false,
      showWordsCounter: false,
      showStatusbar: false,
      placeholder: "",
    }),
    []
  );

  const [item, setItem] = useState({
    name: "",
    description: "",
    tags: [],
    brand_id: "",
    image: "",
    is_active: true,
    awards: [],
  });

  const [errors, setErrors] = useState({});
  const imageKey = useRef("");

  const handleChange = useCallback((field, value) => {
    setItem((p) => ({ ...p, [field]: value }));
    setErrors((e) => ({ ...e, [field]: "" }));
  }, []);
  const [isImageUploading, setIsImageUploading] = useState(false);

  // ---------- FILE UPLOAD ----------
  const sendFile = (file) => {
    setIsImageUploading(true);
    const formData = new FormData();
    formData.append("entity_type", "case_study");
    formData.append("image", file);

    axios
      .post("/s3/save-file", formData)
      .then((res) => {
        imageKey.current = res.data.data.file_key;
        setItem((p) => ({
          ...p,
          image: URL.createObjectURL(file),
        }));
        setIsImageUploading(false);
      })
      .catch(console.error);
  };

  const handleImageDrop = useCallback((_drop, accepted) => {
    if (!accepted[0]) return;
    sendFile(accepted[0]);
  }, []);

  // ----------- VALIDATION -----------
  const validateFields = () => {
    let hasError = false;
    const newErrors = {};

    if (!item.name.trim()) {
      newErrors.name = "Name is required";
      hasError = true;
    }
    if (!item.description.trim()) {
      newErrors.description = "Description is required";
      hasError = true;
    }
    if (!item.brand_id) {
      newErrors.brand_id = "Brand is required";
      hasError = true;
    }
    if (!item.image) {
      setFileError("Please upload an image");
      hasError = true;
    }

    setErrors(newErrors);
    return !hasError;
  };
  const [awardsOptions, setAwardsOptions] = useState([]);

  // ---------- LOAD BRANDS + EDIT ----------
  useEffect(() => {
    axios
      .get("/brands")
      .then((res) => {
        console.log(res.data.data);
        setBrands(
          res.data?.data?.data?.map((b) => ({
            label: b.name,
            value: String(b.id),
          })) || []
        );
      })
      .catch(console.error);
    axios.get("/awards").then((res) => {
      const data = res?.data?.data?.data || [];
      setAwardsOptions(
        data.map((p) => ({
          label: p.title,
          value: String(p.id),
        }))
      );
    });

    if (mode === "edit") {
      axios
        .get(`/case-studies/${id}`)
        .then((res) => {
          const d = res.data.data;

          setItem({
            name: d.project_name || "",
            description: d.description || "",
            tags: d.tags || [],
            brand_id: d.brand_id ? String(d.brand_id) : "",
            image: d.image_url || "",
            is_active: d.is_active ? true : false,
            awards:
              d.awards?.map((m) => ({
                label: m.title,
                value: String(m.id),
              })) || [],
          });

          setIsSaving(false);
        })
        .catch(console.error);
    }
  }, []);

  // ---------- SAVE ----------
  const handleSave = () => {
    if (!validateFields()) return;

    setIsSaving(true);
    const formData = new FormData();

    if (mode === "edit") formData.append("_method", "PATCH");

    formData.append("project_name", item.name);
    formData.append("description", item.description);
    formData.append("brand_id", item.brand_id);
    formData.append("is_active", item.is_active ? 1 : 0);
    item.awards.forEach((member, i) =>
      formData.append(`awards[${i}]`, member.value)
    );

    // tags
    item.tags.forEach((t) => formData.append("tags[]", t));

    if (imageKey.current) formData.append("image", imageKey.current);

    axios
      .post(mode === "new" ? "/case-studies" : `/case-studies/${id}`, formData)
      .then(() => navigate("/admin/case-studies"))
      .catch(console.error)
      .finally(() => setIsSaving(false));
  };

  return (
    <Page
      title={`${mode === "new" ? "Add" : "Edit"} Item`}
      breadcrumbs={[{ onAction: () => navigate("/admin/case-studies") }]}
    >
      {isSaving && <Loading />}

      <Card sectioned>
        <FormLayout>
          {/* -------- IMAGE -------- */}
          <Text>Image</Text>
          <div style={{ display: "flex" }}>
            <Thumbnail size="large" source={item.image || ""} />

            <div style={{ width: "100%", marginLeft: 10 }}>
              <DropZone
                accept=".png,.jpg,.jpeg,.webp"
                allowMultiple={false}
                onDrop={handleImageDrop}
              >
                <DropZone.FileUpload actionHint="Upload an image" />
              </DropZone>
            </div>
          </div>
          {fileError && <InlineError message={fileError} />}

          {/* -------- NAME -------- */}
          <TextField
            label="Name"
            value={item.name}
            onChange={(v) => handleChange("name", v)}
            error={errors.name}
          />

          {/* -------- DESCRIPTION -------- */}
          <Text>Description</Text>
          <JoditEditor
            value={item.description}
            onChange={(v) => handleChange("description", v)}
            config={editorConfig}
          />
          {errors.description && <InlineError message={errors.description} />}

          {/* -------- TAGS -------- */}
          <Text>Tags</Text>
          <TagsInput
            value={item.tags}
            onChange={(tags) => handleChange("tags", tags)}
          />

          {/* -------- BRAND -------- */}
          <Select
            label="Brand"
            options={brands}
            value={item.brand_id}
            onChange={(v) => handleChange("brand_id", v)}
            error={errors.brand_id}
            placeholder="Select brand"
          />
          <Text>Awards</Text>
          <div style={{ position: "relative", zIndex: 9999 }}>
            <MultiSelect
              options={awardsOptions}
              value={item.awards}
              onChange={(v) => handleChange("awards", v)}
              labelledBy="Select Awards"
            />
            {errors.assignees && <InlineError message={errors.assignees} />}
          </div>

          {/* -------- ACTIVE -------- */}
          <Checkbox
            label="Is Active"
            checked={item.is_active}
            onChange={(v) => handleChange("is_active", v)}
          />
        </FormLayout>
      </Card>

      <PageActions
        primaryAction={{
          content: "Save",
          onAction: handleSave,
          loading: isSaving,
        }}
        secondaryActions={
          mode === "edit" && [
            {
              content: "Delete",
              destructive: true,
              loading: isSaving && true,
              onClick: () => {
                setIsSaving(true);
                axios
                  .delete(`/case-studies/${id}`)
                  .then((result) => {
                    navigate(`/admin/case-studies`);
                  })
                  .catch((err) => console.log(err));
              },
            },
          ]
        }
      />

      {isImageUploading && <LoadingScreen />}
    </Page>
  );
};

export default AddEditCaseStudy;
